import React from "react";
import RaciTaskRow from "./RaciTaskRow";
import { JobRow } from "./QueueingTab";
import { Metric, Shift, eliminationNote } from "./MetricsPanel";
import {
  RACI_ROLES,
  ROLE_LABELS,
  PARTICIPANT_CURRENCY,
} from "../../utils/whatif/whatIfNormalize";
import {
  formatAmount,
  formatCount,
  formatDurationShort,
  formatPercent,
} from "../../utils/whatif/whatIfFormat";
import {
  deriveQueueJobs,
  readStaffCount,
} from "../../utils/whatif/whatIfQueueing";
import { buildScenarioToBePayload } from "../../utils/whatif/whatIfScenarioToBe";
import { createWhatIfScenarioToBe } from "../../services/whatIfScenarioToBeService";

const GRID_COLUMNS = `2.4fr ${RACI_ROLES.map(() => "1fr").join(" ")}`;

// Same as-is/to-be transition as the as-is RaciMatrix's own cost line — kept
// as its own copy there too, rather than a shared import, to avoid a
// circular dependency (ScenarioEditor renders this tab).
const CostSummary = ({ label, original, current, money }) => {
  const delta = current - original;
  const percent = original !== 0 ? (delta / original) * 100 : 0;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <div className="wif-raci-summary-item">
      <span className="wif-raci-summary-label">{label}</span>
      <span className="wif-raci-summary-value">
        {money(original)}
        <span className="wif-compare-arrow" aria-hidden="true">
          {" "}
          →{" "}
        </span>
        {money(current)}
      </span>
      {delta !== 0 && (
        <span className={`wif-delta wif-delta--${direction}`}>
          {delta >= 0 ? "+" : "−"}
          {money(Math.abs(delta))}
          {original !== 0 && ` (${formatPercent(percent, 1)})`}
        </span>
      )}
    </div>
  );
};

// Adapts a scenario's computed task entry — whatIfScenario keeps slots as one
// flat list tagged with `slot.role` — into the shape RaciTaskRow already knows
// how to render (participants grouped by role), so the to-be matrix reuses the
// exact same row component as the as-is one instead of a second renderer that
// could drift from it.
const toRaciRowTask = (entry) => {
  const participantsByRole = RACI_ROLES.reduce((byRole, role) => {
    byRole[role] = entry.slots
      .filter(({ slot }) => slot.role === role)
      .map(({ slot, added, changed, minutes, costUsd, unassigned }) => ({
        jobId: slot.jobId,
        jobName: unassigned ? "Unassigned" : slot.jobName,
        role,
        minutes,
        allocationPercent: slot.allocationPercent,
        hourlyRateUsd: slot.hourlyRateUsd,
        costUsd,
        source: added ? "added" : changed ? "reassigned" : "original",
      }));
    return byRole;
  }, {});

  const participantCount = RACI_ROLES.reduce(
    (sum, role) =>
      sum + participantsByRole[role].filter((p) => p.jobId != null).length,
    0,
  );

  return {
    taskId: entry.task.taskId,
    taskName: entry.task.taskName,
    eliminated: entry.eliminated,
    totalCostUsd: entry.costUsd,
    minutesTotal: entry.minutes,
    participantCount,
    participantsByRole,
  };
};

// Final review before committing a scenario: the to-be metrics, the resulting
// RACI matrix, and the queueing table exactly as it last ran — nothing here is
// editable, so what the user saves is what they see.
const SaveScenarioTab = ({
  scenario,
  processId,
  companyId,
  cycleTimeAsIs,
  cycleTimeToBe,
  laborCostPerCase,
  laborCostPerCaseToBe,
  timeUnit,
  currency,
  rates,
  queueing,
}) => {
  const money = (amountUsd) =>
    formatAmount(amountUsd, PARTICIPANT_CURRENCY, currency, rates);
  const duration = (minutes) => formatDurationShort(minutes, timeUnit);

  const toBeTasks = React.useMemo(
    () => scenario.tasks.map(toRaciRowTask),
    [scenario.tasks],
  );

  const jobs = React.useMemo(() => deriveQueueJobs(scenario), [scenario]);
  const hasQueueingResult = Boolean(queueing?.result);
  const byJobId = queueing?.result?.byJobId || {};
  const note = eliminationNote(scenario.eliminatedCount);

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [savedScenario, setSavedScenario] = React.useState(null);

  const handleSave = async () => {
    if (!processId || saving) return;
    setSaving(true);
    setSaveError("");
    try {
      const payload = buildScenarioToBePayload({
        scenario,
        queueing,
        processId,
        companyId,
        name: name.trim(),
        description: description.trim(),
        laborCostPerCase,
        laborCostPerCaseToBe,
        cycleTimeToBe,
        timeUnit,
        currency,
      });
      const created = await createWhatIfScenarioToBe(payload);
      setSavedScenario(created);
    } catch (error) {
      setSaveError(error?.message || "Could not save this scenario.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <p className="muted">
        The final to-be picture after every change made in the Tasks, Job
        Allocation and Queuing tabs — review it here before saving.
      </p>

      <div className="metrics-grid wif-save-metrics">
        <Metric
          label="Cycle Time"
          value={
            <Shift
              from={duration(cycleTimeAsIs)}
              to={duration(cycleTimeToBe)}
            />
          }
          note={note}
        />
        <Metric
          label="Labour Cost / Case"
          value={
            <Shift
              from={money(laborCostPerCase)}
              to={money(laborCostPerCaseToBe)}
            />
          }
          note={note}
        />
        <Metric
          label="Staffing Cost"
          value={
            <Shift
              from={money(scenario.originalGrossCostUsd)}
              to={money(scenario.costUsd)}
            />
          }
          note={note}
        />
      </div>

      <div className="section-title">RACI Assignments (To-Be)</div>
      <div className="roles-table wif-raci-table">
        <div
          className="roles-table-header wif-raci-header-row"
          style={{ gridTemplateColumns: GRID_COLUMNS }}
        >
          <div className="cell">Task</div>
          {RACI_ROLES.map((role) => (
            <div
              className={`cell wif-raci-head wif-raci-head--${role}`}
              key={role}
            >
              <span className="wif-raci-dot" aria-hidden="true" />
              {ROLE_LABELS[role]}{" "}
              <span className="wif-raci-head-key">({role})</span>
            </div>
          ))}
        </div>

        <div className="roles-table-body as-table">
          {toBeTasks.map((task) => (
            <RaciTaskRow
              key={task.taskId}
              task={task}
              gridTemplateColumns={GRID_COLUMNS}
              timeUnit={timeUnit}
              currency={currency}
              rates={rates}
            />
          ))}
        </div>

        <div className="wif-raci-summary">
          <div className="wif-raci-summary-item">
            <span className="wif-raci-summary-label">Tasks</span>
            <span className="wif-raci-summary-value">
              {scenario.eliminatedCount > 0
                ? `${formatCount(toBeTasks.length - scenario.eliminatedCount)} (${formatCount(scenario.eliminatedCount)} NVA)`
                : formatCount(toBeTasks.length)}
            </span>
          </div>
          <div className="wif-raci-summary-item">
            <span className="wif-raci-summary-label">Attributed effort</span>
            <span className="wif-raci-summary-value">
              {duration(scenario.minutes)}
            </span>
          </div>
          <CostSummary
            label="Staffing cost"
            original={scenario.originalGrossCostUsd}
            current={scenario.costUsd}
            money={money}
          />
        </div>
      </div>

      <div className="section-title" style={{ marginTop: 20 }}>
        Queuing Analysis (Last Run)
      </div>
      {!hasQueueingResult ? (
        <p className="muted">
          No queueing analysis has been run yet. Run one from the Queuing
          Analysis tab, then come back here to review the final numbers.
        </p>
      ) : (
        <div className="wif-q-table">
          <div className="wif-q-row wif-q-head">
            <div className="wif-q-cell wif-q-cell--job">Job</div>
            <div className="wif-q-cell wif-q-cell--staff">Staff</div>
            <div className="wif-q-cell wif-q-cell--num">Service / case</div>
            <div className="wif-q-cell wif-q-cell--num">Weekly load</div>
            <div className="wif-q-cell wif-q-cell--num">Utilization</div>
            <div className="wif-q-cell wif-q-cell--num">Queue wait / case</div>
          </div>

          {jobs.map((job) => (
            <JobRow
              key={job.key}
              job={job}
              computed={byJobId[String(job.jobId)] || null}
              isBottleneck={
                queueing.result?.bottleneckJobId != null &&
                String(queueing.result.bottleneckJobId) === String(job.jobId)
              }
              staff={readStaffCount(
                queueing.staffByJob,
                job.jobId,
                job.recordedStaffCount,
              )}
              timeUnit={timeUnit}
              readOnly
            />
          ))}
        </div>
      )}

      <div className="wif-save-form">
        <div className="form-group">
          <label htmlFor="wif-save-name">Scenario name</label>
          <input
            id="wif-save-name"
            type="text"
            className="form-input"
            placeholder="e.g. Reassign Marketing Analyst, eliminate 1 step"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="wif-save-description">Description (optional)</label>
          <textarea
            id="wif-save-description"
            className="form-input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      {saveError && (
        <ul className="whatif-warnings">
          <li>{saveError}</li>
        </ul>
      )}

      {savedScenario && !saveError && (
        <p className="wif-save-success">
          Scenario saved{savedScenario?.id != null ? ` (#${savedScenario.id})` : ""}. You
          can compare it against another saved scenario from the Compare
          Scenario tab.
        </p>
      )}

      <div className="wif-save-actions">
        <button
          type="button"
          className="primary-btn"
          disabled={!processId || saving}
          title={!processId ? "No process selected." : undefined}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save Scenario"}
        </button>
      </div>
    </>
  );
};

export default SaveScenarioTab;
